import React, { useState } from "react";
import { Box, Chip, Collapse, Typography } from "@mui/material";
import VisibilityOutlinedIcon from "@mui/icons-material/VisibilityOutlined";
import PhotoCameraOutlinedIcon from "@mui/icons-material/PhotoCameraOutlined";
import InfoOutlinedIcon from "@mui/icons-material/InfoOutlined";

import palette from "../../../common/styles/palette";
import {
  AiAnalysis,
  AiVisualAnalysis,
} from "../../../services/sheai/SheAiChecklistTypes";
import { perguntaDoItem } from "./checklistReport";
import {
  linkifyTimestamps,
  tsToSeconds,
} from "../debriefing-checklist/timestampLinks";

/**
 * "Análise visual complementar": o que a IMAGEM sustenta, e o que ela não.
 *
 * Leitura separada do relatório por eixo de propósito. O relatório responde
 * "como o avaliado foi"; esta seção responde "o quanto disso o vídeo prova".
 * É o que o professor precisa antes de assinar o Veredito: onde a evidência é
 * visual e onde ela depende de áudio, medição ou feedback do manequim.
 *
 * Por isso `status` classifica a EVIDÊNCIA, não o desempenho — um Item pode ser
 * `visivel_compativel` aqui e sair `partially_done` no veredito por faltar a
 * verbalização exigida. Nunca derivar um do outro.
 *
 * O bloco só existe nas rodadas do agente que emitem `visual_analysis`; quando
 * falta, a seção não renderiza (nada a inventar a partir do veredito).
 */

/** Rótulo e cor de cada `status` que o agente emite. */
const STATUS: Record<
  string,
  { rotulo: string; cor: string; fundo: string }
> = {
  visivel_compativel: {
    rotulo: "Visível compatível",
    cor: palette.emeraldDeep,
    fundo: palette.emeraldSoft,
  },
  requer_outras_fontes: {
    rotulo: "Requer outras fontes",
    cor: palette.warning,
    fundo: palette.warningSoft,
  },
};

const FACE_VISIBILITY: Record<
  string,
  { rotulo: string; cor: string; fundo: string }
> = {
  adequada: {
    rotulo: "Rosto visível",
    cor: palette.emeraldDeep,
    fundo: palette.emeraldSoft,
  },
  parcial: {
    rotulo: "Visibilidade parcial",
    cor: palette.warning,
    fundo: palette.warningSoft,
  },
  indisponivel: {
    rotulo: "Rosto não avaliável",
    cor: palette.textMuted,
    fundo: palette.muted,
  },
};

const ChecklistVisualSection: React.FC<{
  visual?: AiVisualAnalysis | null;
  /** A análise da rodada — de onde sai o enunciado de cada `item_id`. */
  analise?: AiAnalysis | null;
  /** Enunciado por `item_id`, quando a tela já carregou o Checklist. */
  perguntaPorItem?: Record<string, string>;
  /**
   * URL da gravação — habilita o preview expansível: cada timestamp da
   * evidência vira uma pill que abre o vídeo naquele instante, dentro do
   * próprio card do item. Sem ela as pills caem no seek best-effort do
   * player principal (que esta aba não tem).
   */
  videoUrl?: string | null;
}> = ({ visual, analise, perguntaPorItem, videoUrl }) => {
  const rawItems = visual?.items;
  const itens = Array.isArray(rawItems) ? rawItems : [];
  const temConteudo = !!visual?.summary || itens.length > 0;
  const temObservacoes = itens.some(
    (item) => (item.behavioral_observations?.length || 0) > 0
  );
  // Preview do trecho: qual item está expandido e em que instante. Clicar na
  // mesma pill fecha; em outra pill do mesmo item, troca o trecho.
  const [preview, setPreview] = useState<{
    itemId: string;
    tsStart: string;
    tsEnd?: string;
  } | null>(null);

  const alternarPreview = (
    itemId: number | string,
    tsStart: string,
    tsEnd?: string
  ) => {
    if (!videoUrl) return;
    const id = String(itemId);
    setPreview((atual) =>
      atual?.itemId === id &&
      atual?.tsStart === tsStart &&
      atual?.tsEnd === tsEnd
        ? null
        : { itemId: id, tsStart, tsEnd }
    );
  };

  if (!temConteudo) return null;

  const enunciado = (itemId: number | string) => {
    const doVeredito = (analise?.items || []).find(
      (item) => String(item.item_id) === String(itemId)
    );
    return doVeredito
      ? perguntaDoItem(doVeredito, perguntaPorItem)
      : perguntaPorItem?.[String(itemId)] || `Item ${itemId}`;
  };

  return (
    <Box
      sx={{
        mb: 4,
        p: 2.5,
        borderRadius: `${palette.radius}px`,
        backgroundColor: palette.surface,
        border: `1px solid ${palette.border}`,
        boxShadow: palette.shadowSm,
      }}
    >
      <Box sx={{ display: "flex", alignItems: "center", gap: 1, mb: 1.5 }}>
        <VisibilityOutlinedIcon sx={{ color: palette.brand, fontSize: 20 }} />
        <Typography
          variant="h6"
          sx={{ fontWeight: 700, color: palette.brandDark, lineHeight: 1.2 }}
        >
          Análise visual complementar
        </Typography>
      </Box>

      {visual?.summary && (
        <Typography variant="body2" sx={{ color: palette.text, mb: 1.5 }}>
          {visual.summary}
        </Typography>
      )}

      {visual?.frame_quality && (
        <Box
          sx={{
            display: "flex",
            alignItems: "flex-start",
            gap: 1,
            px: 2,
            py: 1.25,
            mb: 2.5,
            borderRadius: "8px",
            backgroundColor: palette.muted,
          }}
        >
          <PhotoCameraOutlinedIcon
            sx={{ fontSize: 16, color: palette.textMuted, mt: 0.25 }}
          />
          <Typography variant="body2" sx={{ color: palette.textMuted }}>
            {visual.frame_quality}
          </Typography>
        </Box>
      )}

      {temObservacoes && (
        <Box
          sx={{
            display: "flex",
            alignItems: "flex-start",
            gap: 1,
            px: 2,
            py: 1.25,
            mb: 2.5,
            borderRadius: "8px",
            backgroundColor: palette.warningSoft,
            border: `1px solid ${palette.warning}`,
          }}
        >
          <InfoOutlinedIcon
            sx={{ fontSize: 18, color: palette.warning, mt: 0.15 }}
          />
          <Typography variant="body2" sx={{ color: palette.text }}>
            Esta leitura descreve sinais faciais e comportamentais observáveis
            e levanta hipóteses para revisão. Não reconhece emoções, não altera
            a nota e precisa ser validada pelo professor no trecho do vídeo.
          </Typography>
        </Box>
      )}

      {itens.length > 0 && (
        <>
          <Typography
            variant="body2"
            sx={{ fontWeight: 700, color: palette.text, mb: 1.25 }}
          >
            Apoio visual por item do checklist
          </Typography>

          <Box sx={{ display: "grid", gap: 1.25 }}>
            {itens.map((item, posicao) => {
              const marca =
                STATUS[String(item.status)] ?? {
                  rotulo: String(item.status || "sem classificação"),
                  cor: palette.textMuted,
                  fundo: palette.muted,
                };

              return (
                <Box
                  key={`${item.item_id}-${posicao}`}
                  sx={{
                    px: 2,
                    py: 1.5,
                    borderRadius: "8px",
                    backgroundColor: palette.background,
                    border: `1px solid ${palette.border}`,
                  }}
                >
                  <Box
                    sx={{
                      display: "flex",
                      alignItems: "baseline",
                      gap: 1,
                      flexWrap: "wrap",
                    }}
                  >
                    <Typography
                      sx={{
                        fontSize: "0.75rem",
                        fontWeight: 700,
                        color: palette.lilacMid,
                      }}
                    >
                      {String(posicao + 1).padStart(2, "0")}
                    </Typography>
                    <Typography
                      sx={{ fontWeight: 700, color: palette.text }}
                      variant="body2"
                    >
                      {enunciado(item.item_id)}
                    </Typography>
                    <Chip
                      size="small"
                      label={marca.rotulo}
                      sx={{
                        height: 20,
                        fontSize: "0.6875rem",
                        fontWeight: 700,
                        textTransform: "uppercase",
                        backgroundColor: marca.fundo,
                        color: marca.cor,
                      }}
                    />
                  </Box>

                  {item.evidence && (
                    <Typography
                      variant="body2"
                      component="div"
                      sx={{ color: palette.textMuted, mt: 0.75 }}
                    >
                      <Box component="span" sx={{ fontWeight: 700 }}>
                        Evidência:
                      </Box>{" "}
                      {linkifyTimestamps(item.evidence, (ts) =>
                        alternarPreview(item.item_id, ts)
                      )}
                    </Typography>
                  )}

                  {item.limitation && (
                    <Typography
                      variant="body2"
                      component="div"
                      sx={{
                        color: palette.textMuted,
                        fontStyle: "italic",
                        mt: 0.5,
                      }}
                    >
                      <Box component="span" sx={{ fontWeight: 700 }}>
                        Limitação:
                      </Box>{" "}
                      {linkifyTimestamps(item.limitation, (ts) =>
                        alternarPreview(item.item_id, ts)
                      )}
                    </Typography>
                  )}

                  {(item.behavioral_observations?.length || 0) > 0 && (
                    <Box
                      sx={{
                        mt: 1.25,
                        p: 1.5,
                        borderRadius: "8px",
                        backgroundColor: palette.surface,
                        border: `1px solid ${palette.border}`,
                      }}
                    >
                      <Typography
                        variant="body2"
                        sx={{ color: palette.text, fontWeight: 700, mb: 1 }}
                      >
                        Sinais faciais e comportamentais no contexto
                      </Typography>

                      <Box sx={{ display: "grid", gap: 1 }}>
                        {item.behavioral_observations?.map(
                          (observation, observationIndex) => {
                            const visibilidade =
                              FACE_VISIBILITY[observation.face_visibility] ??
                              FACE_VISIBILITY.indisponivel;
                            const clareza =
                              typeof observation.confidence === "number"
                                ? `${Math.round(
                                    Math.max(
                                      0,
                                      Math.min(1, observation.confidence)
                                    ) * 100
                                  )}%`
                                : null;

                            return (
                              <Box
                                key={`${observation.ts_start}-${observation.ts_end}-${observationIndex}`}
                                sx={{
                                  p: 1.25,
                                  borderRadius: "8px",
                                  backgroundColor: palette.background,
                                }}
                              >
                                <Box
                                  sx={{
                                    display: "flex",
                                    alignItems: "center",
                                    gap: 0.75,
                                    flexWrap: "wrap",
                                  }}
                                >
                                  <Chip
                                    size="small"
                                    label={`${observation.ts_start}–${observation.ts_end}`}
                                    clickable={Boolean(videoUrl)}
                                    onClick={
                                      videoUrl
                                        ? () =>
                                            alternarPreview(
                                              item.item_id,
                                              observation.ts_start,
                                              observation.ts_end
                                            )
                                        : undefined
                                    }
                                    sx={{
                                      height: 22,
                                      fontSize: "0.6875rem",
                                      fontWeight: 700,
                                      color: palette.brand,
                                      backgroundColor: palette.brandSoft,
                                    }}
                                  />
                                  <Chip
                                    size="small"
                                    label={visibilidade.rotulo}
                                    sx={{
                                      height: 22,
                                      fontSize: "0.6875rem",
                                      fontWeight: 700,
                                      color: visibilidade.cor,
                                      backgroundColor: visibilidade.fundo,
                                    }}
                                  />
                                  {clareza && (
                                    <Typography
                                      variant="caption"
                                      sx={{ color: palette.textMuted }}
                                    >
                                      Clareza dos sinais: {clareza}
                                    </Typography>
                                  )}
                                </Box>

                                {observation.observable_cues.length > 0 && (
                                  <Box
                                    component="ul"
                                    sx={{
                                      color: palette.textMuted,
                                      fontSize: "0.8125rem",
                                      my: 0.75,
                                      pl: 2.25,
                                    }}
                                  >
                                    {observation.observable_cues.map(
                                      (cue, cueIndex) => (
                                        <li key={`${cue}-${cueIndex}`}>{cue}</li>
                                      )
                                    )}
                                  </Box>
                                )}

                                {observation.hypothesis && (
                                  <Typography
                                    variant="body2"
                                    sx={{ color: palette.text, mt: 0.5 }}
                                  >
                                    <Box
                                      component="span"
                                      sx={{ fontWeight: 700 }}
                                    >
                                      Hipótese a validar:
                                    </Box>{" "}
                                    {observation.hypothesis}
                                  </Typography>
                                )}

                                {observation.limitation && (
                                  <Typography
                                    variant="body2"
                                    sx={{
                                      color: palette.textMuted,
                                      fontStyle: "italic",
                                      mt: 0.5,
                                    }}
                                  >
                                    <Box
                                      component="span"
                                      sx={{ fontWeight: 700 }}
                                    >
                                      Limite da leitura:
                                    </Box>{" "}
                                    {observation.limitation}
                                  </Typography>
                                )}
                              </Box>
                            );
                          }
                        )}
                      </Box>
                    </Box>
                  )}

                  {/* Preview do trecho citado: expande dentro do card do
                      item, já posicionado no instante da pill clicada. */}
                  {videoUrl && (
                    <Collapse
                      in={preview?.itemId === String(item.item_id)}
                      unmountOnExit
                    >
                      <Box
                        sx={{
                          mt: 1.25,
                          borderRadius: "8px",
                          overflow: "hidden",
                          backgroundColor: palette.videoShell,
                        }}
                      >
                        {preview?.itemId === String(item.item_id) && (
                          <video
                            key={`${preview.tsStart}-${preview.tsEnd || ""}`}
                            src={videoUrl}
                            controls
                            autoPlay
                            style={{
                              display: "block",
                              width: "100%",
                              maxHeight: 360,
                            }}
                            onLoadedMetadata={(evento) => {
                              const segundos = tsToSeconds(preview.tsStart);
                              if (segundos != null) {
                                evento.currentTarget.currentTime = segundos;
                              }
                            }}
                            onTimeUpdate={(evento) => {
                              const fim = preview.tsEnd
                                ? tsToSeconds(preview.tsEnd)
                                : null;
                              if (
                                fim != null &&
                                evento.currentTarget.currentTime >= fim
                              ) {
                                evento.currentTarget.pause();
                              }
                            }}
                          />
                        )}
                      </Box>
                    </Collapse>
                  )}
                </Box>
              );
            })}
          </Box>
        </>
      )}
    </Box>
  );
};

export default ChecklistVisualSection;
